import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import { makeStyles } from "@material-ui/core/styles";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";
import InternalTicketsModal from "../InternalTicketsModal";
import TicketTagsModal from "../TicketTagsModal";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	menuItem: {
		fontSize: "0.88rem",
		fontWeight: 500,
		paddingTop: 9,
		paddingBottom: 9,
	},
	menuIcon: {
		minWidth: 34,
		color: theme.palette.text.secondary,
		"& svg": { fontSize: 19 },
	},
	menuItemDanger: {
		color: theme.palette.type === "dark" ? "#fca5a5" : "#c4292e",
		"& $menuIcon": {
			color: theme.palette.type === "dark" ? "#fca5a5" : "#c4292e",
		},
	},
}));

const TicketOptionsMenu = ({
	ticket,
	menuOpen,
	handleClose,
	anchorEl,
	onRefreshTicket,
}) => {
	const classes = useStyles();
	const history = useHistory();
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [internalTicketsModalOpen, setInternalTicketsModalOpen] = useState(false);
	const [ticketTagsModalOpen, setTicketTagsModalOpen] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
			window.dispatchEvent(
				new CustomEvent("ticket:updated", {
					detail: { ticket: null, deletedTicketId: ticket.id }
				})
			);
			history.push("/tickets");
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = e => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleOpenTransferModal = e => {
		setTransferTicketModalOpen(true);
		handleClose();
	};

	const handleOpenInternalTicketsModal = () => {
		setInternalTicketsModalOpen(true);
		handleClose();
	};

	const handleOpenTicketTagsModal = () => {
		setTicketTagsModalOpen(true);
		handleClose();
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	const handleCloseInternalTicketsModal = () => {
		if (isMounted.current) {
			setInternalTicketsModalOpen(false);
		}
	};

	const handleCloseTicketTagsModal = () => {
		if (isMounted.current) {
			setTicketTagsModalOpen(false);
		}
	};

	return (
		<>
			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleClose}
			>
				<MenuItem
					className={classes.menuItem}
					onClick={handleOpenInternalTicketsModal}
				>
					<ListItemIcon className={classes.menuIcon}>
						<AssignmentOutlinedIcon />
					</ListItemIcon>
					Tickets internos
				</MenuItem>
				<MenuItem
					className={classes.menuItem}
					onClick={handleOpenTicketTagsModal}
				>
					<ListItemIcon className={classes.menuIcon}>
						<LocalOfferOutlinedIcon />
					</ListItemIcon>
					Tags
				</MenuItem>
				<MenuItem
					className={classes.menuItem}
					onClick={handleOpenTransferModal}
				>
					<ListItemIcon className={classes.menuIcon}>
						<SyncAltIcon />
					</ListItemIcon>
					{i18n.t("ticketOptionsMenu.transfer")}
				</MenuItem>
				<Can
					role={user.profile}
					perform="ticket-options:deleteTicket"
					yes={() => (
						<MenuItem
							className={`${classes.menuItem} ${classes.menuItemDanger}`}
							onClick={handleOpenConfirmationModal}
						>
							<ListItemIcon className={classes.menuIcon}>
								<DeleteOutlineIcon />
							</ListItemIcon>
							{i18n.t("ticketOptionsMenu.delete")}
						</MenuItem>
					)}
				/>
			</Menu>
			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
					ticket.id
				} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
					ticket.contact.name
				}?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				ticketWhatsappId={ticket.whatsappId}
			/>
			<TicketTagsModal
				open={ticketTagsModalOpen}
				onClose={handleCloseTicketTagsModal}
				ticket={ticket}
				onSaved={onRefreshTicket}
			/>
			<InternalTicketsModal
				open={internalTicketsModalOpen}
				onClose={handleCloseInternalTicketsModal}
				ticketId={ticket.id}
				internalTickets={ticket.internalTickets || []}
				onRefresh={onRefreshTicket}
			/>
		</>
	);
};

export default TicketOptionsMenu;
